import { useState, useEffect, useMemo } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, X, Receipt, TrendingDown, DollarSign } from 'lucide-react';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { BUSINESS_TZ } from '@/lib/format';

interface Transaction {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
    type: string;
}

export interface RecordSaleForm {
    buyerName: string;
    buyerPhone: string;
    buyerAddress: string;
    transactionIds: number[];
    expectedAmount: number;
    notes: string;
}

/** Data to pre-populate the sheet for editing an existing sale */
export interface SaleInitialData {
    saleId: number;
    buyerId: number;
    buyerName: string;
    buyerPhone: string;
    buyerAddress: string;
    expectedAmount: number;
    notes: string;
    transactions: Transaction[];
}

interface RecordSaleSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (form: RecordSaleForm) => void;
    isPending: boolean;
    /** When provided, the sheet opens in edit mode with pre-populated data */
    initialData?: SaleInitialData | null;
}

export function RecordSaleSheet({ open, onOpenChange, onSubmit, isPending, initialData }: RecordSaleSheetProps) {
    const [buyerName, setBuyerName] = useState('');
    const [buyerPhone, setBuyerPhone] = useState('');
    const [buyerAddress, setBuyerAddress] = useState('');
    const [expectedAmount, setExpectedAmount] = useState('');
    const [notes, setNotes] = useState('');

    // Multi-transaction state
    const [selectedTransactions, setSelectedTransactions] = useState<Transaction[]>([]);
    const [showTransactionPicker, setShowTransactionPicker] = useState(false);

    const isEditMode = !!initialData;

    useEffect(() => {
        if (open) {
            if (initialData) {
                // Edit mode: pre-populate
                setBuyerName(initialData.buyerName);
                setBuyerPhone(initialData.buyerPhone);
                setBuyerAddress(initialData.buyerAddress);
                setExpectedAmount(String(initialData.expectedAmount));
                setNotes(initialData.notes);
                setSelectedTransactions(initialData.transactions);
            } else {
                // Create mode: reset
                setBuyerName('');
                setBuyerPhone('');
                setBuyerAddress('');
                setExpectedAmount('');
                setNotes('');
                setSelectedTransactions([]);
            }
            setShowTransactionPicker(false);
        }
    }, [open, initialData]);

    const totalReceived = useMemo(
        () => selectedTransactions.reduce((sum, t) => sum + t.amount, 0),
        [selectedTransactions],
    );

    const expectedNum = parseFloat(expectedAmount) || 0;
    const outstanding = expectedNum - totalReceived;

    const canSubmit = buyerName.trim() && expectedNum > 0 && selectedTransactions.length > 0;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            buyerName: buyerName.trim(),
            buyerPhone: buyerPhone.trim(),
            buyerAddress: buyerAddress.trim(),
            transactionIds: selectedTransactions.map(t => t.id),
            expectedAmount: expectedNum,
            notes: notes.trim(),
        });
    };

    const handleTransactionSelected = (id: number | null, transaction?: Transaction) => {
        if (!id || !transaction) return;
        // Avoid duplicates
        if (selectedTransactions.some(t => t.id === id)) return;
        setSelectedTransactions(prev => [...prev, transaction]);
        setShowTransactionPicker(false);
    };

    const removeTransaction = (id: number) => {
        setSelectedTransactions(prev => prev.filter(t => t.id !== id));
    };

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[90vw] sm:!w-[540px] !max-w-none rounded-2xl border shadow-2xl bg-background p-0 flex flex-col"
            >
                {/* Header */}
                <div className="p-6 pb-4 space-y-1 border-b">
                    <SheetTitle className="text-2xl font-bold">{isEditMode ? 'Edit Vehicle Sale' : 'Record Vehicle Sale'}</SheetTitle>
                    <p className="text-muted-foreground text-sm">
                        {isEditMode
                            ? 'Update buyer details, expected amount, or add/remove transactions.'
                            : 'Enter the buyer details, expected amount, and link transactions.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Buyer Info */}
                        <div className="space-y-4">
                            <h3 className="text-xs uppercase text-muted-foreground tracking-wide font-semibold flex items-center gap-2">
                                <Receipt className="h-3.5 w-3.5" /> Buyer Information
                            </h3>
                            <div className="grid gap-3">
                                <div className="grid gap-1.5">
                                    <Label htmlFor="buyer-name" className="text-sm">Buyer Name *</Label>
                                    <Input id="buyer-name" placeholder="Full name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} autoFocus required />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="grid gap-1.5">
                                        <Label htmlFor="buyer-phone" className="text-sm">Phone</Label>
                                        <Input id="buyer-phone" placeholder="e.g. 0241234567" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} />
                                    </div>
                                    <div className="grid gap-1.5">
                                        <Label htmlFor="buyer-address" className="text-sm">Address</Label>
                                        <Input id="buyer-address" placeholder="Location" value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Expected Amount */}
                        <div className="space-y-3">
                            <h3 className="text-xs uppercase text-muted-foreground tracking-wide font-semibold flex items-center gap-2">
                                <DollarSign className="h-3.5 w-3.5" /> Sale Amount
                            </h3>
                            <div className="grid gap-1.5">
                                <Label htmlFor="expected-amount" className="text-sm">Expected Total *</Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">GH₵</span>
                                    <Input
                                        id="expected-amount"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={expectedAmount}
                                        onChange={(e) => setExpectedAmount(e.target.value)}
                                        className="pl-12 font-mono"
                                        required
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">The agreed sale price for this vehicle.</p>
                            </div>
                        </div>

                        {/* Transactions */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs uppercase text-muted-foreground tracking-wide font-semibold flex items-center gap-2">
                                    <Receipt className="h-3.5 w-3.5" /> Linked Transactions *
                                </h3>
                                <span className="text-xs text-muted-foreground">{selectedTransactions.length} selected</span>
                            </div>

                            {/* Selected transaction cards */}
                            {selectedTransactions.length > 0 && (
                                <div className="space-y-2">
                                    {selectedTransactions.map((t) => (
                                        <div
                                            key={t.id}
                                            className="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-2">
                                                    <span className="font-mono font-semibold text-sm text-green-600 dark:text-green-400">
                                                        {formatCurrency(t.amount)}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {new Date(t.transaction_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: BUSINESS_TZ })}
                                                    </span>
                                                </div>
                                                {t.description && (
                                                    <p className="text-xs text-muted-foreground truncate mt-0.5">{t.description}</p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeTransaction(t.id)}
                                                className="p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add Transaction button or inline picker */}
                            {showTransactionPicker ? (
                                <div className="space-y-2">
                                    <TransactionSelector
                                        value={null}
                                        onChange={handleTransactionSelected}
                                        defaultType="CREDIT"
                                        placeholder="Select a sale transaction..."
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowTransactionPicker(false)}
                                        className="text-xs text-muted-foreground"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowTransactionPicker(true)}
                                    className="w-full gap-2 border-dashed"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Transaction
                                </Button>
                            )}

                            {/* Running totals */}
                            {selectedTransactions.length > 0 && expectedNum > 0 && (
                                <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Received</span>
                                        <span className="font-mono font-medium text-green-600 dark:text-green-400">{formatCurrency(totalReceived)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Expected</span>
                                        <span className="font-mono font-medium">{formatCurrency(expectedNum)}</span>
                                    </div>
                                    <div className="border-t pt-2 flex justify-between">
                                        <span className="font-medium flex items-center gap-1.5">
                                            <TrendingDown className="h-3.5 w-3.5" />
                                            Outstanding
                                        </span>
                                        <span className={`font-mono font-bold ${outstanding > 0 ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>
                                            {formatCurrency(Math.abs(outstanding))}
                                            {outstanding <= 0 && ' ✓'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Notes */}
                        <div className="space-y-3">
                            <div className="grid gap-1.5">
                                <Label htmlFor="sale-notes" className="text-sm">Notes</Label>
                                <Textarea
                                    id="sale-notes"
                                    placeholder="Optional notes about this sale..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    className="resize-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 pt-4 border-t flex justify-end gap-2">
                        <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" disabled={!canSubmit || isPending}>
                            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {isEditMode ? 'Save Changes' : 'Record Sale'}
                        </Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}
